import { useLocation } from "wouter";
import { Phone, Droplet, Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { services } from "@/data/services";
import { cities } from "@/data/cities";
import { CanonicalLink as Link } from "@/components/CanonicalLink";

export function Navbar() {
  const [location] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "instant" });

  const closeMenu = () => {
    setIsOpen(false);
    scrollTop();
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md py-3" : "bg-white/95 backdrop-blur-sm py-5"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 md:px-6 flex items-center justify-between gap-2 md:gap-4">
        <Link href="/" onClick={scrollTop} className="flex items-center gap-2 group shrink-0">
          <div className="bg-primary text-white p-2 rounded-lg group-hover:bg-accent transition-colors">
            <Droplet className="h-6 w-6" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-sm sm:text-base md:text-2xl text-primary tracking-tight">
              Plomberie <span className="block sm:inline text-accent">Excellence</span>
            </span>
          </div>
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden lg:flex items-center gap-2">
          <NavigationMenu>
            <NavigationMenuList>
              
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link href="/" onClick={scrollTop} className={`group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent disabled:pointer-events-none disabled:opacity-50 ${location === "/" ? "text-accent" : "text-foreground/80"}`}>
                    Accueil
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={`h-10 px-4 py-2 text-sm font-medium bg-transparent transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent data-[active]:bg-accent/10 data-[state=open]:bg-accent/10 ${location.startsWith("/services") ? "text-accent" : "text-foreground/80"}`}>
                  Services
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid max-h-[calc(100dvh-7rem)] overflow-y-auto w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link href={`/services/${s.slug}`} onClick={scrollTop} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent">
                          <div className={`text-sm font-semibold leading-none ${s.isEmergency ? "text-red-600 flex items-center gap-1.5" : "text-primary"}`}>
                            {s.isEmergency && <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>}
                            {s.shortTitle}
                          </div>
                          <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1.5">
                            {s.description}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={`h-10 px-4 py-2 text-sm font-medium bg-transparent transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent data-[active]:bg-accent/10 data-[state=open]:bg-accent/10 ${location.startsWith("/plombier-") || location === "/zones" ? "text-accent" : "text-foreground/80"}`}>
                  Zones
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[300px] gap-3 p-4">
                    <li>
                      <Link href="/plombier-vieux-longueuil" onClick={scrollTop} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent">
                        <div className="text-sm font-semibold leading-none text-primary flex items-center justify-between">
                           Vieux-Longueuil
                        </div>
                      </Link>
                    </li>
                    {cities.filter((c) => c.slug !== "vieux-longueuil").map((c) => (
                      <li key={c.slug}>
                        <Link href={`/plombier-${c.slug}`} onClick={scrollTop} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent">
                          <div className="text-sm font-semibold leading-none text-primary flex items-center justify-between">
                            {c.name}
                          </div>
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link href="/plombier-rive-sud" onClick={scrollTop} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent">
                        <div className="text-sm font-semibold leading-none text-primary flex items-center justify-between">
                          Rive-Sud de Montréal
                        </div>
                      </Link>
                    </li>
                    <div className="my-1 border-t border-border"></div>
                    <li>
                      <Link href="/zones" onClick={scrollTop} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors bg-gray-50 hover:bg-accent/10 hover:text-accent">
                        <div className="text-sm font-semibold leading-none text-primary text-center">Toutes nos zones →</div>
                      </Link>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link href="/a-propos/" onClick={scrollTop} className={`group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent ${location.replace(/\/$/, "") === "/a-propos" ? "text-accent" : "text-foreground/80"}`}>
                    À propos
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link href="/contact" onClick={scrollTop} className={`group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent disabled:pointer-events-none disabled:opacity-50 ${location === "/contact" ? "text-accent" : "text-foreground/80"}`}>
                    Contact
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a href="tel:4389081143" className="inline-flex items-center justify-center bg-accent hover:bg-accent/90 text-white font-bold px-3 sm:px-4 md:px-6 h-10 md:h-11 rounded-md shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm md:text-base">
            <Phone className="mr-1.5 md:mr-2 h-4 md:h-5 w-4 md:w-5" />
            <span className="hidden sm:inline">(438) 908-1143</span>
            <span className="inline sm:hidden">Appeler</span>
          </a>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" aria-label="Ouvrir le menu de navigation">
                <Menu className="h-5 w-5 text-primary" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] sm:w-[400px] overflow-y-auto pt-16">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <SheetDescription className="sr-only">Accédez aux services, aux zones desservies et aux coordonnées.</SheetDescription>
              <div className="flex flex-col gap-6">
                <Link href="/" onClick={closeMenu} className="font-bold text-lg text-primary border-b pb-2">
                  Accueil
                </Link>
                
                <div className="space-y-3">
                  <h4 className="font-bold text-lg text-primary border-b pb-2">Nos Services</h4>
                  <ul className="flex flex-col gap-3 pl-2">
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link href={`/services/${s.slug}`} onClick={closeMenu} className={`font-medium ${s.isEmergency ? "text-red-600" : "text-foreground/80"} hover:text-accent flex items-center gap-2`}>
                          {s.isEmergency && <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>}
                          {s.shortTitle}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-3">
                  <h4 className="font-bold text-lg text-primary border-b pb-2">Zones Desservies</h4>
                  <ul className="flex flex-col gap-3 pl-2">
                    <li>
                      <Link href="/plombier-vieux-longueuil" onClick={closeMenu} className="font-medium text-foreground/80 hover:text-accent flex items-center justify-between">
                        Vieux-Longueuil
                      </Link>
                    </li>
                    {cities.filter((c) => c.slug !== "vieux-longueuil").map((c) => (
                      <li key={c.slug}>
                        <Link href={`/plombier-${c.slug}`} onClick={closeMenu} className="font-medium text-foreground/80 hover:text-accent flex items-center justify-between">
                          {c.name}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link href="/plombier-rive-sud" onClick={closeMenu} className="font-medium text-foreground/80 hover:text-accent flex items-center justify-between">
                        Rive-Sud de Montréal
                      </Link>
                    </li>
                    <li>
                      <Link href="/zones" onClick={closeMenu} className="font-medium text-accent hover:underline">
                        Toutes nos zones →
                      </Link>
                    </li>
                  </ul>
                </div>

                <Link href="/contact" onClick={closeMenu} className="font-bold text-lg text-primary border-b pb-2">
                  Contact
                </Link>
                <Link href="/a-propos/" onClick={closeMenu} className="font-bold text-lg text-primary border-b pb-2">
                  À propos
                </Link>

                <div className="pt-4 pb-10">
                  <a href="tel:4389081143" className="flex items-center justify-center w-full bg-accent hover:bg-accent/90 text-white font-bold h-12 rounded-md shadow-md text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    (438) 908-1143
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
